// ============================================
// Chat Messaging - Stream (ES6)
// 스트리밍 메시지 처리
// ============================================

import { preprocessAIContent, normalizeBlockContent, formatDialogueText } from './formatter.js';
import { getCurrentChatData, getCurrentProfile } from '../../app/state.js';
import { parseAIContentStreaming, formatAIMessage, getCharacterAvatar, renderAvatarHTML, getCurrentCharacterName } from '../parser.js';
import { escapeHtml } from '../../utils.js';
import { setupMessageLongPress } from '../context-menu.js';

// ============================================
// 스트리밍 상태
// ============================================

let streamingState = {
  targetText: '',       // 최종 표시할 텍스트
  displayedText: '',    // 현재 표시된 텍스트
  typingInterval: null, // 타이핑 인터벌
  isTyping: false,
  renderedBlockCount: 0, // 이미 렌더링된 블록 수 (증분 업데이트용)
  userQuoteMessages: [], // 스트리밍 중 생성된 user-quote 메시지들
  lastUserQuoteDiv: null, // 현재 타이핑 중인 user-quote 메시지
  splitAIMessages: [] // user-quote 분할로 생성된 AI 메시지들
};

export function getStreamingState() {
  return streamingState;
}

export function setStreamingTargetText(text) {
  const isFirstText = !streamingState.targetText && text;
  streamingState.targetText = text;

  // 첫 데이터 도착 시 로딩 인디케이터 즉시 제거 (타이핑 시작 전)
  if (isFirstText) {
    const streamingDiv = document.querySelector('.message.streaming');
    if (streamingDiv) {
      const indicator = streamingDiv.querySelector('.typing-indicator');
      if (indicator) {
        indicator.remove();
      }
    }
  }
}

// ============================================
// 스트리밍 메시지 생성
// ============================================

export function createStreamingMessage() {
  const container = document.getElementById('messagesContainer');

  // Remove empty state if exists
  const emptyState = container.querySelector('.empty-state');
  if (emptyState) emptyState.remove();

  // 상태 초기화
  streamingState = {
    targetText: '',
    displayedText: '',
    typingInterval: null,
    isTyping: false,
    renderedBlockCount: 0,
    userQuoteMessages: [],
    lastUserQuoteDiv: null,
    splitAIMessages: []
  };

  const div = document.createElement('div');
  div.className = 'message assistant streaming';
  div.dataset.timestamp = new Date().getTime();

  div.innerHTML = `
    <div class="message-content streaming-content"></div>
    <div class="typing-indicator">
      <div class="loading"><span></span><span></span><span></span></div>
    </div>
  `;

  container.appendChild(div);

  requestAnimationFrame(() => {
    container.scrollTop = container.scrollHeight;
  });

  // 타이핑 시작
  startTypingEffect(div);

  return div;
}

// ============================================
// 타이핑 효과
// ============================================

function startTypingEffect(div) {
  if (streamingState.typingInterval) return;

  const TYPING_SPEED = 15; // ms per character (조절 가능)

  streamingState.typingInterval = setInterval(() => {
    // 전처리된 실제 표시할 텍스트
    const visibleText = preprocessAIContent(streamingState.targetText, true);

    // 표시할 텍스트가 없으면 로딩만 표시
    if (!visibleText || visibleText.length === 0) {
      return; // 타이핑 인디케이터만 표시된 상태 유지
    }

    if (streamingState.displayedText.length < visibleText.length) {
      // 한 글자씩 추가
      streamingState.displayedText = visibleText.slice(
        0,
        streamingState.displayedText.length + 1
      );

      // 증분 업데이트: 이미 렌더링된 블록은 유지, 새로운/변경된 블록만 업데이트
      const contentDiv = div.querySelector('.streaming-content');
      if (contentDiv) {
        updateStreamingContentIncremental(contentDiv, streamingState.displayedText);
      }

      // 스크롤은 움직이지 않음 - 사용자가 자유롭게 읽을 수 있도록
    }
  }, TYPING_SPEED);
}

// ============================================
// 증분 업데이트
// ============================================

function updateStreamingContentIncremental(contentDiv, text) {
  // {{user}} 마커 처리
  const currentChatData = getCurrentChatData();
  const currentProfile = getCurrentProfile();
  const userName = currentChatData?.userProfile?.name || currentProfile?.name || null;
  let processedText = text;
  if (userName) {
    processedText = processedText.replace(/\{\{user\}\}/gi, userName);
  } else {
    processedText = processedText.replace(/\{\{user\}\}/gi, '{{USER_PLACEHOLDER}}');
  }

  let escaped = escapeHtml(processedText);
  escaped = escaped.replace(/\{\{USER_PLACEHOLDER\}\}/g, '<span class="user-var-placeholder">{{user}}</span>');

  // 스트리밍 중 불완전한 마커 제거 (예: "{", "{U", "{C1" 등)
  // 완전한 마커는 {N}, {C}, {T}, {H}, {I}, {S}, {U}, {X/이름/} 형태
  escaped = escaped.replace(/\{[NCTSHIUX]?[0-9]*(?:\/[^/]*)?$/g, '');

  const blocks = parseAIContentStreaming(escaped);

  const totalBlocks = blocks.length;
  const renderedCount = streamingState.renderedBlockCount;
  const container = document.getElementById('messagesContainer');
  const streamingDiv = container.querySelector('.message.streaming');

  // 마지막 블록용 컨테이너 참조 (새 블록 삽입 위치 결정용)
  let lastBlockContainer = contentDiv.querySelector('.streaming-last-block');

  // 이미 렌더링된 블록 수보다 현재 블록이 많으면 새 블록 추가
  if (totalBlocks > renderedCount) {
    // 마지막 블록이 user-quote가 아닌데 lastUserQuoteDiv가 있으면 → 이전 user-quote가 완료됨
    // lastUserQuoteDiv를 userQuoteMessages로 이동하고 리셋
    const lastBlock = blocks[totalBlocks - 1];
    if (lastBlock.type !== 'user-quote' && streamingState.lastUserQuoteDiv) {
      streamingState.lastUserQuoteDiv.classList.remove('streaming-user-quote');
      streamingState.userQuoteMessages.push(streamingState.lastUserQuoteDiv);
      streamingState.lastUserQuoteDiv = null;
      // 이전 user-quote 블록은 이미 렌더링됨, renderedBlockCount 증가
      streamingState.renderedBlockCount++;
    }

    // 완료된 블록들 추가 (마지막 블록 제외 - 아직 타이핑 중일 수 있음)
    for (let i = streamingState.renderedBlockCount; i < totalBlocks - 1; i++) {
      const block = blocks[i];

      if (block.type === 'user-quote') {
        // user-quote는 별도 메시지로 생성
        renderUserQuoteMessage(block, streamingDiv, container);
      } else {
        // 일반 블록은 AI 컨테이너에 추가
        const blockHtml = renderSingleBlock(block);
        if (lastBlockContainer) {
          lastBlockContainer.insertAdjacentHTML('beforebegin', blockHtml);
        } else {
          contentDiv.insertAdjacentHTML('beforeend', blockHtml);
        }
      }
      streamingState.renderedBlockCount++;
    }
  }

  // 마지막 블록 (현재 타이핑 중인 블록) 업데이트
  const lastBlockIndex = totalBlocks - 1;
  if (lastBlockIndex >= 0) {
    const lastBlock = blocks[lastBlockIndex];

    if (lastBlock.type === 'user-quote') {
      // 마지막 블록이 user-quote면 별도 메시지로 처리
      updateLastUserQuote(lastBlock, streamingDiv, container);
    } else {
      // 일반 블록은 streaming-last-block에서 타이핑
      const lastBlockHtml = renderSingleBlock(lastBlock);

      // 마지막 블록용 컨테이너가 없으면 생성
      if (!lastBlockContainer) {
        lastBlockContainer = document.createElement('div');
        lastBlockContainer.className = 'streaming-last-block';
        contentDiv.appendChild(lastBlockContainer);
      }

      // 마지막 블록만 업데이트 (이미지가 있어도 같은 블록이면 유지됨)
      const speakerKey = lastBlock.speaker !== undefined ? String(lastBlock.speaker) : '';
      const prevType = lastBlockContainer.dataset.blockType;
      const prevSpeaker = lastBlockContainer.dataset.blockSpeaker;

      if (prevType !== lastBlock.type || prevSpeaker !== speakerKey) {
        // 블록 타입이 바뀌면 전체 교체
        const isDialogueOrThought = (t) => t === 'dialogue' || t === 'thought';
        if (isDialogueOrThought(prevType) && isDialogueOrThought(lastBlock.type) && prevSpeaker === speakerKey) {
          updateLastBlockContent(lastBlockContainer, lastBlock);
          const blockEl = lastBlockContainer.querySelector('.dialogue-block, .thought-block');
          if (blockEl) {
            blockEl.className = lastBlock.type === 'dialogue' ? 'dialogue-block' : 'thought-block';
          }
        } else {
          lastBlockContainer.innerHTML = lastBlockHtml;
        }
        lastBlockContainer.dataset.blockType = lastBlock.type;
        lastBlockContainer.dataset.blockSpeaker = speakerKey;
      } else {
        updateLastBlockContent(lastBlockContainer, lastBlock);
      }
    }
  }
}

// 현재 AI 콘텐츠를 별도 메시지로 분할 (user-quote 삽입 전)
function splitCurrentAIContent(streamingDiv, container) {
  const contentDiv = streamingDiv.querySelector('.streaming-content');
  if (!contentDiv) return;

  // 현재 콘텐츠가 있는지 확인 (streaming-last-block 제외)
  const existingBlocks = contentDiv.querySelectorAll('.ai-content-block');
  const lastBlockContainer = contentDiv.querySelector('.streaming-last-block');

  // streaming-last-block 내용도 포함해서 복사할 콘텐츠가 있는지 확인
  const hasLastBlockContent = lastBlockContainer && lastBlockContainer.innerHTML.trim();

  if (existingBlocks.length === 0 && !hasLastBlockContent) return;

  // 새 AI 메시지 div 생성 (분할된 이전 콘텐츠용)
  const splitDiv = document.createElement('div');
  splitDiv.className = 'message assistant split-ai-message';
  splitDiv.dataset.timestamp = streamingDiv.dataset.timestamp;

  const splitContentDiv = document.createElement('div');
  splitContentDiv.className = 'message-content';

  // 기존 블록들 복사
  existingBlocks.forEach(block => {
    splitContentDiv.appendChild(block.cloneNode(true));
  });

  // streaming-last-block 내용도 복사
  if (hasLastBlockContent) {
    const lastBlockClone = document.createElement('div');
    lastBlockClone.innerHTML = lastBlockContainer.innerHTML;
    // 각 자식을 복사
    Array.from(lastBlockClone.children).forEach(child => {
      splitContentDiv.appendChild(child.cloneNode(true));
    });
  }

  splitDiv.appendChild(splitContentDiv);

  // 스트리밍 메시지 앞에 분할된 메시지 삽입
  container.insertBefore(splitDiv, streamingDiv);
  streamingState.splitAIMessages.push(splitDiv);

  // 원래 streaming 콘텐츠 비우기
  contentDiv.innerHTML = '';

  // streaming-last-block 다시 생성 (이후 콘텐츠용)
  const newLastBlock = document.createElement('div');
  newLastBlock.className = 'streaming-last-block';
  contentDiv.appendChild(newLastBlock);
}

// user-quote 블록을 별도 메시지로 렌더링 (완료된 블록용)
function renderUserQuoteMessage(block, streamingDiv, container) {
  // user-quote 삽입 전에 현재 AI 콘텐츠 분할
  splitCurrentAIContent(streamingDiv, container);

  const normalizedContent = normalizeBlockContent(block.content);
  const formattedContent = formatDialogueText(normalizedContent);

  const userQuoteDiv = document.createElement('div');
  userQuoteDiv.className = 'message user user-quote-from-ai';
  const contentDiv = document.createElement('div');
  contentDiv.className = 'message-content';
  contentDiv.innerHTML = formattedContent;
  userQuoteDiv.appendChild(contentDiv);

  // 스트리밍 메시지 바로 앞에 삽입 (분할 후이므로 올바른 위치)
  container.insertBefore(userQuoteDiv, streamingDiv);
  streamingState.userQuoteMessages.push(userQuoteDiv);
}

// 마지막 user-quote 블록 업데이트 (타이핑 중)
function updateLastUserQuote(block, streamingDiv, container) {
  const normalizedContent = normalizeBlockContent(block.content);
  const formattedContent = formatDialogueText(normalizedContent);

  // 현재 타이핑 중인 user-quote 메시지 찾기 또는 생성
  let lastUserQuote = streamingState.lastUserQuoteDiv;

  if (!lastUserQuote) {
    // user-quote 삽입 전에 현재 AI 콘텐츠 분할
    splitCurrentAIContent(streamingDiv, container);

    // 새 user-quote 메시지 생성
    lastUserQuote = document.createElement('div');
    lastUserQuote.className = 'message user user-quote-from-ai streaming-user-quote';
    const contentDiv = document.createElement('div');
    contentDiv.className = 'message-content';
    lastUserQuote.appendChild(contentDiv);
    container.insertBefore(lastUserQuote, streamingDiv);
    streamingState.lastUserQuoteDiv = lastUserQuote;
  }

  // 내용 업데이트
  const contentEl = lastUserQuote.querySelector('.message-content');
  if (contentEl) contentEl.innerHTML = formattedContent;
}

// ============================================
// 블록 렌더링
// ============================================

function renderSingleBlock(block) {
  const normalizedContent = normalizeBlockContent(block.content);
  // 모든 텍스트 블록에 **볼드** + *행동지문* 처리 적용
  const formattedContent = formatDialogueText(normalizedContent);
  if (block.type === 'narration') {
    return `<div class="ai-content-block"><div class="narration-block">${formattedContent}</div></div>`;
  } else if (block.type === 'image') {
    const imageIndex = parseInt(block.content.trim(), 10);
    const chatData = getCurrentChatData();
    const imageUrl = !isNaN(imageIndex) ? (chatData?.introImages?.[imageIndex] || '') : '';
    if (imageUrl) {
      return `<div class="ai-content-block"><div class="intro-image-block-chat"><img src="${imageUrl}" alt="이미지" data-action="showImage" data-url="${imageUrl}" data-name="이미지" onerror="this.parentElement&&(this.parentElement.style.display='none')"></div></div>`;
    }
    return '';
  } else if (block.type === 'situational') {
    const imageIndex = parseInt(block.content.trim(), 10);
    const chatData = getCurrentChatData();
    const situationalImages = chatData?.situationalImages || [];
    const imageData = !isNaN(imageIndex) ? situationalImages[imageIndex] : null;
    if (imageData?.imageUrl) {
      const altText = escapeHtml(imageData.description || '상황 이미지');
      return `<div class="ai-content-block"><div class="situational-image-block-chat"><img src="${imageData.imageUrl}" alt="${altText}" data-action="showImage" data-url="${imageData.imageUrl}" data-name="${altText}" onerror="this.parentElement&&(this.parentElement.style.display='none')"></div></div>`;
    }
    return '';
  } else if (block.type === 'helper') {
    return `<div class="ai-content-block"><div class="helper-block"><div class="helper-avatar">🌟</div><div class="helper-content"><div class="helper-speaker">별님이</div><div class="helper-bubble"><div class="helper-text">${formattedContent}</div></div></div></div></div>`;
  } else if (block.type === 'thought') {
    const avatarData = block.isExtra ? { type: 'extra' } : getCharacterAvatar(block.speaker);
    const avatarHTML = renderAvatarHTML(avatarData);
    const speakerName = block.speaker || getCurrentCharacterName();
    return `<div class="ai-content-block"><div class="thought-block"><div class="thought-avatar">${avatarHTML}</div><div class="thought-content"><div class="thought-speaker">${speakerName}</div><div class="thought-bubble"><div class="thought-text">${formattedContent}</div></div></div></div></div>`;
  } else if (block.type === 'dialogue') {
    const avatarData = block.isExtra ? { type: 'extra' } : getCharacterAvatar(block.speaker);
    const avatarHTML = renderAvatarHTML(avatarData);
    const speakerName = block.speaker || getCurrentCharacterName();
    return `<div class="ai-content-block"><div class="dialogue-block"><div class="dialogue-avatar">${avatarHTML}</div><div class="dialogue-content"><div class="dialogue-speaker">${speakerName}</div><div class="dialogue-bubble"><div class="dialogue-text">${formattedContent}</div></div></div></div></div>`;
  } else if (block.type === 'user-quote') {
    // user-quote는 별도의 유저 메시지로 표시됨 (handleUserQuoteBlocks에서 처리)
    return '';
  }
  return `<div class="ai-content-block">${formattedContent}</div>`;
}

// 마지막 블록의 텍스트 내용만 업데이트 (이미지/아바타 유지)
function updateLastBlockContent(container, block) {
  const normalizedContent = normalizeBlockContent(block.content);
  // 모든 텍스트 블록에 **볼드** + *행동지문* 처리 적용
  const formattedContent = formatDialogueText(normalizedContent);

  if (block.type === 'narration') {
    const textEl = container.querySelector('.narration-block');
    if (textEl) textEl.innerHTML = formattedContent;
  } else if (block.type === 'helper') {
    const textEl = container.querySelector('.helper-text');
    if (textEl) textEl.innerHTML = formattedContent;
  } else if (block.type === 'thought') {
    const textEl = container.querySelector('.thought-text');
    if (textEl) textEl.innerHTML = formattedContent;
  } else if (block.type === 'dialogue') {
    const textEl = container.querySelector('.dialogue-text');
    if (textEl) textEl.innerHTML = formattedContent;
  } else if (block.type === 'user-quote') {
    const textEl = container.querySelector('.user-quote-content');
    if (textEl) textEl.innerHTML = formattedContent;
  }
}

// ============================================
// 스트리밍 완료/중단
// ============================================

export function finalizeStreamingMessage(div, fullContent, turn = null) {
  if (streamingState.typingInterval) {
    clearInterval(streamingState.typingInterval);
    streamingState.typingInterval = null;
  }

  div.classList.remove('streaming');

  // 타이핑 인디케이터 완전 제거
  const indicator = div.querySelector('.typing-indicator');
  if (indicator) indicator.remove();

  // streaming-last-block 클래스 제거
  const lastBlock = div.querySelector('.streaming-last-block');
  if (lastBlock) {
    lastBlock.classList.remove('streaming-last-block');
  }

  const contentDiv = div.querySelector('.message-content');

  if (contentDiv) {
    contentDiv.classList.remove('streaming-content');

    // 분할된 AI 메시지가 있으면 합쳐서 원래대로 복원
    if (streamingState.splitAIMessages.length > 0 || streamingState.userQuoteMessages.length > 0) {
      // 스트리밍 중 생성된 분할 메시지들과 user-quote 모두 제거
      streamingState.splitAIMessages.forEach(msg => msg.remove());
      streamingState.userQuoteMessages.forEach(msg => msg.remove());
      if (streamingState.lastUserQuoteDiv) {
        streamingState.lastUserQuoteDiv.remove();
      }
    }

    // 최종 콘텐츠로 렌더링 (전처리 포함) - 항상 전체 렌더링
    // user-quote는 AI 메시지 안에 원래 위치에 표시됨 (parser.js에서 처리)
    contentDiv.innerHTML = formatAIMessage(fullContent);
  }

  // {U} 태그가 있으면 has-user-quote 클래스 추가
  if (fullContent && fullContent.includes('{U}')) {
    div.classList.add('has-user-quote');
  }

  // turn 저장 (길게 누르기 메뉴에서 사용)
  if (turn) {
    div.dataset.turn = turn;
  }
  div.dataset.rawContent = fullContent;

  // 길게 누르기로 컨텍스트 메뉴 표시
  setupMessageLongPress(div, fullContent);

  // 상태 초기화
  streamingState = {
    targetText: '',
    displayedText: '',
    typingInterval: null,
    isTyping: false,
    renderedBlockCount: 0,
    userQuoteMessages: [],
    lastUserQuoteDiv: null,
    splitAIMessages: []
  };
}

export function stopStreaming() {
  if (streamingState.typingInterval) {
    clearInterval(streamingState.typingInterval);
  }
  // 스트리밍 중단 시 user-quote 메시지 제거
  streamingState.userQuoteMessages.forEach(msg => msg.remove());
  if (streamingState.lastUserQuoteDiv) {
    streamingState.lastUserQuoteDiv.remove();
  }
  // 분할된 AI 메시지들도 제거
  streamingState.splitAIMessages.forEach(msg => msg.remove());
  streamingState = {
    targetText: '',
    displayedText: '',
    typingInterval: null,
    isTyping: false,
    renderedBlockCount: 0,
    userQuoteMessages: [],
    lastUserQuoteDiv: null,
    splitAIMessages: []
  };
}

